import { createElement as h } from "react"

import Autocomplete from "@mui/material/Autocomplete"
import { flow } from "fp-ts/es6/function"
import T from "prop-types"
import { length, lte, nth, prop } from "ramda"
import { cx } from "ramda-extension/es"

import { Logic } from "@cirrus/fe-lib"

import * as css from "./css"
import { TextField } from "../../atoms"

export const Combobox = ({
  blurOnSelect = true,
  clearOnBlur = false,
  disableCloseOnSelect = false,
  freeSolo = false,
  height,
  width = 300,
  limitTags = 10,
  label,
  onInputChange,
  optionLabel,
  selectOnFocus,
  startAdornment,
  InputProps = {},
  triggerLength = 1,
  placeholder,
  disabled,
  forcePopupIcon = false,
  ...props
}) =>
  h(Autocomplete, {
    autoComplete: true,
    blurOnSelect,
    disableCloseOnSelect,
    freeSolo,
    selectOnFocus,

    classes: {
      ...css.useStaticOverrides(),

      root: cx(
        prop("root", css.useStaticOverrides()),
        prop("root", css.useDynamicOverrides({ height })),
      ),
    },

    clearOnBlur,
    disabled,
    limitTags,
    forcePopupIcon,
    getOptionLabel: optionLabel,
    onInputChange: Logic.when(flow(nth(1), length, lte(triggerLength)), onInputChange),
    openOnFocus: true,
    renderInput: (params) =>
      h(TextField, {
        disabled,
        height,
        label,
        multipleValues: props.multiple,
        params,
        placeholder,
        startAdornment,
        width,
        ...InputProps,
      }),

    ...props,
  })

Combobox.propTypes = {
  /**
   * Props for <a href="/?path=/docs/components-atoms-text-field--basic">TextField</a>.
   * It's used as an input element of the combobox.
   */
  InputProps: T.shape(TextField.propTypes),

  blurOnSelect: T.bool,
  clearOnBlur: T.bool,
  disabled: T.bool,
  endAdornment: T.oneOfType([T.elementType, T.node]),
  groupedBy: T.func,
  height: T.oneOfType([T.number, T.string]),
  label: T.string,
  loading: T.bool,
  loadingText: T.string,
  multiple: T.bool,
  noOptionsText: T.string,

  /**
   * Callback fired when the value changes.
   *
   * @param {object} event - The event source of the callback.
   *
   * @param {any} selectedOption - The new value of the component.
   *
   * @param {string} reason
   * One of: "create-option", "select-option", "remove-option", "blur" or "clear".
   *
   * @returns {void}
   */
  onChange: T.func,

  /**
   * Callback fired when the highlight option changes.
   *
   * @param {object} event - The event source of the callback.
   *
   * @param {any} highlightedOption - The highlighted option.
   *
   * @param {string} reason - Can be: "keyboard", "auto", "mouse".
   *
   * @returns {void}
   */
  onHighlightChange: T.func,

  /**
   * Callback fired when the input value changes.
   *
   * @param {object} event - The event source of the callback.
   *
   * @param {string} inputValue - The new value of the text input.
   *
   * @param {string} reason
   * Can be: "input" (user input), "reset" (programmatic change), "clear".
   *
   * @returns {void}
   */
  onInputChange: T.func,

  open: T.bool,
  optionLabel: T.func,
  options: T.array.isRequired,
  placeholder: T.string,
  startAdornment: T.oneOfType([T.elementType, T.node]),
  triggerLength: T.number,
  value: T.oneOfType([T.string, T.array]),
  width: T.oneOfType([T.number, T.string]),
}

export * from "./lib"
